/**
 * AllThirdParty — Fresh take on the All Third Party page.
 *
 * Copied from materialPivot.js with all cost types combined.
 * Key differences:
 *   1. Uses buildEtcLedger() instead of setUpUserObject()
 *   2. Every DB entry stays as an individual record
 *   3. Multi-entry EtcPopover support
 *   4. 28 combined cost types across all categories
 *   5. Grouped costTypeDescriptions with category labels
 *   6. costTypeCategory map + getCostTypeCategory helper
 *   7. Category-based section grouping
 *   8. FuelAnalysis sub-tab
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { View, Text, TextInput, StyleSheet, Pressable, Icon2, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom, selectedVersionAtom, jobUserDataAtom, phasedForecastAtom,
    jobOpenCommitmentsAtom, jobCostDetailAtom, isLiveVersionAtom, jobPhaseCodesAtom,
    jobUserDataPrevAtom, jobOpenCommitmentsPrevAtom, jobBillingsPrevAtom,
    jobCostDetailPrevAtom, selectedVersionLockedAtom, dataSourceVersionAtom, selectedWIPAtom
} from '../store/company';
import {
    updateUserDataArray, deleteUserData, loadUserData,
    f, fNum, pNum, sum, cN, loadJobOpenCommitmentsCombined, loadCostHistoryCombined
} from '../db/company';
import moment from 'moment';
import { buildColumnTree, calculateColumnTotals } from '../helpers/columnTreeBuilder';
import { buildEtcLedger, getEntryLevel, rekeyEtcMapByColumns } from '../helpers/etcLedger';
import { createBudgetResolver, computePageBudget, computeGrandVariance } from '../helpers/budgetResolver';
import { fieldBonusRemoved, versionStartFor } from '../helpers/fieldBonus';
import { loadResourceCodeTables, buildResourceDropdownFromDb } from '../helpers/resourceCodeDb';
import PivotSelector, { pivotLabels } from './PivotSelector';
import VarianceWeekSelector from './VarianceWeekSelector';
import { ThirdPartyRow, ThirdPartyRowCollapsable, ThirdPartyRowContext } from './ThirdPartyRow';
import { ReversedVoucherLegend } from './reversedVoucher';
import { useTapInvoiceViewer, usePoMatchStatus, invoicedIndex, rowHasVendorInvoice } from './tapInvoiceViewer';
import EtcPopoverHost, { PopoverBreadcrumbContext } from './EtcPopoverHost';
import FuelAnalysis from './fuelAnalysis';

// ── constants ──────────────────────────────────────────────────────────

const availablePivotColumns = ['Phase_Code', 'Discipline', 'Resource_Code', 'Supplier_Name', 'PO_Number', 'Cost_Type_Code', 'Line_ID'];

const tDescriptions = {
    '52200': 'Outside Equipment Rental',
    '53100': 'Materials',
    '53200': 'Mats',
    '53210': 'Mats - Purchase',
    '53300': 'Small Tools – Purchase',
    '53310': 'Small Tools – Rent',
    '53400': 'Skids',
    '53500': 'Consumables',
    '54100': 'Internal Subcontractor',
    '54200': 'External Subcontractor',
    '54300': 'Hauling/Transportation',
    '51800': 'Field Bonus',
    '52300': 'Fuel',
    '52400': 'Repairs',
    '55100': 'Personal Meals',
    '55200': 'Team Meals/Entertain',
    '55300': 'Airfare',
    '55400': 'Lodging',
    '55900': 'Other Travel',
    '56100': 'Field Office Rent',
    '56200': 'Field Office Utility',
    '56300': 'Field Office Expense',
    '56400': 'Lab Fees & Testing',
    '56500': 'Safety & PPE',
    '56600': 'Bonds and Insurance Premiums',
    '56700': 'Permits',
    '56800': 'Insurance Claims',
    '57100': 'Taxes',
    '57200': 'Accrued Expense',
    '57300': 'Warranty Reserve',
    '57900': 'Miscellaneous',
};

const PAGE_COST_TYPES = Object.keys(tDescriptions);

const indentColors = {
    1: '#e8edf2',
    2: '#eef1f6',
    3: '#f3f5f9',
    4: '#f8f9fb',
};

const coCodeType = [
    '51800',
    '52200','52300','52400',
    '53100','53200','53300','53310','53400','53500',
    '54100','54200','54300',
    '55100','55200','55300','55400','55900',
    '56100','56200','56300','56400','56500','56600','56700','56800',
    '57100','57200','57300','57900',
];

const costTypeCategory = {
    '51800': 'Labor',
    '52200': 'Equipment', '52300': 'Equipment', '52400': 'Equipment',
    '53100': 'Materials', '53200': 'Materials', '53300': 'Materials', '53310': 'Materials', '53400': 'Materials', '53500': 'Materials',
    '54100': 'Subcontractors', '54200': 'Subcontractors', '54300': 'Subcontractors',
    '55100': 'Other Expenses', '55200': 'Other Expenses', '55300': 'Other Expenses',
    '55400': 'Other Expenses', '55900': 'Other Expenses',
    '56100': 'Other Expenses', '56200': 'Other Expenses', '56300': 'Other Expenses',
    '56400': 'Other Expenses', '56500': 'Other Expenses', '56600': 'Other Expenses',
    '56700': 'Other Expenses', '56800': 'Other Expenses',
    '57100': 'Other Expenses', '57200': 'Other Expenses', '57300': 'Other Expenses', '57900': 'Other Expenses',
};

const getCostTypeCategory = (costType) => {
    if (costTypeCategory[costType]) return costTypeCategory[costType];
    const prefix = costType?.substring(0, 2);
    if (prefix === '51') return 'Labor';
    if (prefix === '52') return 'Equipment';
    if (prefix === '53') return 'Materials';
    if (prefix === '54') return 'Subcontractors';
    return 'Other Expenses';
};

const costTypeDescriptions = [
    { group: 'Labor', label: 'Field Bonus - 51800', value: '51800' },
    { group: 'Equipment', label: 'Outside Equip Rental - 52200', value: '52200' },
    { group: 'Equipment', label: 'Fuel - 52300', value: '52300' },
    { group: 'Equipment', label: 'Repairs - 52400', value: '52400' },
    { group: 'Materials', label: 'Materials - 53100', value: '53100' },
    { group: 'Materials', label: 'Mats - 53200', value: '53200' },
    { group: 'Materials', label: 'Small Tools – Purchase - 53300', value: '53300' },
    { group: 'Materials', label: 'Small Tools – Rent - 53310', value: '53310' },
    { group: 'Materials', label: 'Skids - 53400', value: '53400' },
    { group: 'Materials', label: 'Consumables - 53500', value: '53500' },
    { group: 'Subcontractors', label: 'Int Sub - 54100', value: '54100' },
    { group: 'Subcontractors', label: 'Ext Sub - 54200', value: '54200' },
    { group: 'Subcontractors', label: 'Hauling/Trans - 54300', value: '54300' },
    { group: 'Other Expenses', label: 'Personal Meals - 55100', value: '55100' },
    { group: 'Other Expenses', label: 'Team Meals - 55200', value: '55200' },
    { group: 'Other Expenses', label: 'Airfare - 55300', value: '55300' },
    { group: 'Other Expenses', label: 'Lodging - 55400', value: '55400' },
    { group: 'Other Expenses', label: 'Other Travel - 55900', value: '55900' },
    { group: 'Other Expenses', label: 'Field Office Rent - 56100', value: '56100' },
    { group: 'Other Expenses', label: 'Field Office Utility - 56200', value: '56200' },
    { group: 'Other Expenses', label: 'Field Office Expense - 56300', value: '56300' },
    { group: 'Other Expenses', label: 'Lab Fees & Testing - 56400', value: '56400' },
    { group: 'Other Expenses', label: 'Safety & PPE - 56500', value: '56500' },
    { group: 'Other Expenses', label: 'Bonds/Insurance - 56600', value: '56600' },
    { group: 'Other Expenses', label: 'Permits - 56700', value: '56700' },
    { group: 'Other Expenses', label: 'Insurance Claims - 56800', value: '56800' },
    { group: 'Other Expenses', label: 'Taxes - 57100', value: '57100' },
    { group: 'Other Expenses', label: 'Accrued Expense - 57200', value: '57200' },
    { group: 'Other Expenses', label: 'Warranty Reserve - 57300', value: '57300' },
    { group: 'Other Expenses', label: 'Miscellaneous - 57900', value: '57900' },
];

const categoryColors = {
    'Labor': { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', badge: '#3b82f6' },
    'Equipment': { bg: '#fef3c7', border: '#fde68a', text: '#92400e', badge: '#f59e0b' },
    'Materials': { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', badge: '#10b981' },
    'Subcontractors': { bg: '#f5f3ff', border: '#ddd6fe', text: '#5b21b6', badge: '#8b5cf6' },
    'Other Expenses': { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', badge: '#ef4444' },
};

// ── Collect descendant entries from a tree node's children ──
const collectChildEntries = (node, entriesByBreadcrumb) => {
    const childEntries = [];
    const walk = (n) => {
        if (!n?.rows || n.rows === false) return;
        Object.values(n.rows).forEach(child => {
            const bc = child.data?.breadcrumb;
            if (bc && entriesByBreadcrumb[bc]) {
                childEntries.push(...entriesByBreadcrumb[bc]);
            }
            walk(child);
        });
    };
    walk(node);
    return childEntries;
};

// ── PivotRowCollapsable ──
const PivotRowCollapsable = React.memo(({ data }) => {
    const ctx = React.useContext(ThirdPartyRowContext);
    const popoverBreadcrumb = React.useContext(PopoverBreadcrumbContext);
    const { entriesByBreadcrumb = {}, setPopoverBreadcrumb, pivotPopoverProps, descriptionVisible } = ctx;
    const allEntries = data?.data?.breadcrumb ? (entriesByBreadcrumb[data.data.breadcrumb] || []) : [];
    const entries = allEntries.filter(e => e.amount !== 0 || e.description || e.notes);
    const allChildEntries = useMemo(() => collectChildEntries(data, entriesByBreadcrumb), [data, entriesByBreadcrumb]);
    const childEntries = allChildEntries.filter(e => e.amount !== 0 || e.description || e.notes);
    const totalCount = entries.length + childEntries.length;
    const isOpen = popoverBreadcrumb === data?.data?.breadcrumb;
    const hasBreadcrumb = !!data?.data?.breadcrumb;

    return (
        <View style={{ position: 'relative' }}>
            <ThirdPartyRowCollapsable data={data} />
            {hasBreadcrumb && (
                <>
                    {totalCount >= 2 && (
                        <Pressable
                            onPress={() => setPopoverBreadcrumb(isOpen ? null : data.data.breadcrumb)}
                            style={{
                                position: 'absolute', top: -12, zIndex: 5, cursor: 'pointer',
                                left: descriptionVisible ? 1885 : 885,
                            }}
                        >
                            <View style={{
                                backgroundColor: isOpen ? '#1d4ed8' : '#3b82f6',
                                borderRadius: 3,
                                padding: '1px 5px', marginLeft: 3, whiteSpace: 'nowrap', lineHeight: 1.2,
                                boxShadow: isOpen ? '0 0 0 2px rgba(59,130,246,0.3)' : '0 1px 2px rgba(0,0,0,0.12)',
                                transition: 'all 0.15s ease',
                            }}>
                                <Text style={{ fontSize: 8, fontWeight: '700', color: 'white', whiteSpace: 'nowrap' }}>{totalCount} val.</Text>
                            </View>
                        </Pressable>
                    )}
                </>
            )}
        </View>
    );
});

// ── component ──────────────────────────────────────────────────────────

const AllThirdParty = () => {
    // ── atoms ──
    const [userData, setUserData] = useAtom(jobUserDataAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobCostHistory] = useAtom(jobCostDetailAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [userDataPrev] = useAtom(jobUserDataPrevAtom);
    const [jobOpenCommitmentsPrev] = useAtom(jobOpenCommitmentsPrevAtom);
    const [jobCostHistoryPrev] = useAtom(jobCostDetailPrevAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    // Scanned vendor-invoice viewer + PO-match rollup (shared with Billing).
    const { openTapDoc, viewerNode } = useTapInvoiceViewer();
    const poStatus = usePoMatchStatus(selectedJob);
    // "Vendor invoice received" filter — keeps only rows whose PO line JDE has
    // vouchered (or direct vouchers). null index = filter off.
    const [invoicedOnly, setInvoicedOnly] = useState(false);
    const invIndex = useMemo(() => (invoicedOnly ? invoicedIndex(poStatus) : null), [invoicedOnly, poStatus]);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [phased] = useAtom(phasedForecastAtom);
    const [phaseDescriptions] = useAtom(jobPhaseCodesAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);

    // Field Bonus (51800) is retired for versions June 18, 2026 forward — hide
    // the row and drop it from every total for those versions only.
    const hideFieldBonus = fieldBonusRemoved(versionStartFor(versions, selectedVersion, selectedWIP));
    const pageDescriptions = useMemo(() => {
        if (!hideFieldBonus) return tDescriptions;
        const { '51800': _omit, ...rest } = tDescriptions;
        return rest;
    }, [hideFieldBonus]);
    const pageCostTypeDescriptions = useMemo(() => (
        hideFieldBonus ? costTypeDescriptions.filter(c => c.value !== '51800') : costTypeDescriptions
    ), [hideFieldBonus]);

    // ── local state ──
    const [objectMap, setObjectMap] = useState({});
    const [objectMapPrev, setObjectMapPrev] = useState({});
    const [openCommitments, setOpenCommitments] = useState([]);
    const [costHistory, setCostHistory] = useState([]);
    const [openCommitmentsPrev, setOpenCommitmentsPrev] = useState([]);
    const [costHistoryPrev, setCostHistoryPrev] = useState([]);
    const [expandedBreadcrumbs, setExpandedBreadcrumbs] = useState({});
    const [phaseDayZero, setPhaseDayZero] = useState({});
    const [dayZeroChangeOrderPhases, setDayZeroChangeOrderPhases] = useState([]);
    const titleDescriptions = pageDescriptions;
    const [resourceDescriptions, setResourceDescriptions] = useState({});
    const [dbResourceCodes, setDbResourceCodes] = useState({ byCode: {}, codesByCostType: {}, mappingByCode: {} });
    const [descriptionVisible, setDescriptionVisible] = useState(false);
    const [collapseAll, setCollapseAll] = useState(false);
    const [collapseNumber, setCollapseNumber] = useState(0);
    const [activeCostTypes, setActiveCostTypes] = useState(() => {
        const init = {}; Object.keys(tDescriptions).forEach(k => init[k] = true); return init;
    });
    const [sortedUserData, setSortedUserData] = useState([]);
    const [deletingRows, setDeletingRows] = useState(new Set());
    const [isExporting, setIsExporting] = useState(false);
    const focusedId = useRef(null);

    const [totalCurrentBudget, setTotalCurrentBudget] = useState(0);
    const [actualTotalTotal, setActualTotalTotal] = useState(0);
    const [openTotalTotal, setOpenTotalTotal] = useState(0);
    const [etcTotalTotal, setEtcTotalTotal] = useState(0);
    const [totalTotalTotal, setTotalTotalTotal] = useState(0);
    const [varianceBudgetTotal, setVarianceBudgetTotal] = useState(0);

    const [varianceWeeks, setVarianceWeeks] = useState([]);
    const [weekTotalMaps, setWeekTotalMaps] = useState({});
    const [weekLoading, setWeekLoading] = useState({});
    const [weekDisplayMode, setWeekDisplayMode] = useState({});
    const [prevGrandTotal, setPrevGrandTotal] = useState(0);

    const [popoverBreadcrumb, setPopoverBreadcrumb] = useState(null);
    const [entriesByBreadcrumb, setEntriesByBreadcrumb] = useState({});
    const [rebuilding, setRebuilding] = useState(false);

    // Budget map — flat { breadcrumb → budget } built from tree, single source of truth for display
    const [budgetMap, setBudgetMap] = useState({});

    const ledgerRef = useRef({ userEtcMap: {}, entriesByBreadcrumb: {} });

    // ── drill columns ──
    const [drillColumns, setDrillColumns] = useState(
        phased === 'phased'
            ? ['Phase_Code', 'Supplier_Name', 'PO_Number', 'Line_ID']
            : ['Supplier_Name', 'PO_Number', 'Line_ID']
    );
    useEffect(() => {
        setDrillColumns(phased === 'phased'
            ? ['Phase_Code', 'Supplier_Name', 'PO_Number', 'Line_ID']
            : ['Supplier_Name', 'PO_Number', 'Line_ID']);
    }, [phased]);

    const path = useMemo(() => [...drillColumns, 'PO_Detail'], [drillColumns]);

    // ── styles (cached) ──
    const _sc = useRef(new Map());
    const _createStyles = (w = null) => StyleSheet.create({
        edit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: w || 100, padding: 2, paddingLeft: 8, textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        notEdit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: w || 100, padding: 2, maxWidth: w || 100, textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        firstNotEdit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: '100%', padding: 2, maxWidth: '100%', textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        title: { width: w || 100, alignItems: 'center', fontSize: 10, fontWeight: '600', padding: '6px 4px', textAlign: 'right', justifyContent: 'flex-end', borderBottom: '1px solid #e5e7eb', backgroundColor: 'transparent', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.6 },
    });
    const s = (w = null) => {
        if (_sc.current.has(w)) return _sc.current.get(w);
        const r = _createStyles(w); _sc.current.set(w, r); return r;
    };

    // ── OC / CH preprocessing ──
    useEffect(() => {
        const oC = [];
        (jobOpenCommitments || []).forEach(e => {
            const d = { ...e };
            if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
            if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
            d.OC = true;
            d.Supplier_Name = d.Supplier_AB_Name;
            d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            d.Line_ID = d.PO_Line_Number || '';
            oC.push(d);
        });
        setOpenCommitments(oC);
    }, [jobOpenCommitments]);

    useEffect(() => {
        const jCH = [];
        (jobCostHistory || []).forEach(d => {
            const c = { ...d };
            c.CH = true;
            c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            jCH.push(c);
        });
        setCostHistory(jCH);
    }, [jobCostHistory]);

    useEffect(() => {
        const oC = [];
        (jobOpenCommitmentsPrev || []).forEach(e => {
            const d = { ...e };
            if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
            if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
            d.OC = true;
            d.Supplier_Name = d.Supplier_AB_Name;
            d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            d.Line_ID = d.PO_Line_Number || '';
            oC.push(d);
        });
        setOpenCommitmentsPrev(oC);
    }, [jobOpenCommitmentsPrev]);

    useEffect(() => {
        const jCH = [];
        (jobCostHistoryPrev || []).forEach(d => {
            const c = { ...d };
            c.CH = true;
            c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            jCH.push(c);
        });
        setCostHistoryPrev(jCH);
    }, [jobCostHistoryPrev]);

    const disciplineMap = useMemo(() => {
        const map = {};
        (userData || []).forEach(d => {
            if (d?.Forecast_Tab === 'Productivity Tracker' && d?.Forecast_Field_Name === 'discipline') {
                map[d?.Forecast_Row_ID] = d?.Forecast_Value;
            }
        });
        return map;
    }, [userData]);

    const reverseDisciplineMap = useMemo(() => {
        const rev = {};
        Object.entries(disciplineMap).forEach(([phase, disc]) => {
            if (!disc) return;
            if (!rev[disc]) rev[disc] = [];
            rev[disc].push(phase);
        });
        return rev;
    }, [disciplineMap]);

    // ── main data processing pipeline ──
    const processDebounceRef = useRef(null);

    useEffect(() => {
        setRebuilding(true);
        if (processDebounceRef.current) clearTimeout(processDebounceRef.current);
        processDebounceRef.current = setTimeout(() => {
            if (!userData || !phaseDescriptions) { setRebuilding(false); return; }

            const ledger = buildEtcLedger(userData, 'All Third Party', drillColumns, disciplineMap);
            ledgerRef.current = ledger;
            setEntriesByBreadcrumb(ledger.entriesByBreadcrumb);
            setPhaseDayZero(ledger.dayZeroBudget);
            // Filter budget to only include All Third Party cost types
            const pageBudget = computePageBudget(ledger.costTypeBudget, PAGE_COST_TYPES);
            setTotalCurrentBudget(fNum(pageBudget));
            setDayZeroChangeOrderPhases(Object.values(ledger.costCodeSeeds));

            const userRows = ledger.allEntries
                .filter(e => e.amount !== 0 || e.notes || e.description)
                .map(e => {
                    const d = e.depth || 1;
                    return {
                        ...e._row,
                        rowId: e._row?.Forecast_Row_ID,
                        Forecast_Row_ID: e.breadcrumb,
                        Forecast_Tab: e._row?.Forecast_Tab || 'All Third Party',
                        Phase_Code: e.Phase_Code,
                        Supplier_Name: d >= 2 ? e.Supplier_Name : '',
                        PO_Number: d >= 3 ? e.PO_Number : '',
                        Line_ID: d >= 4 ? (e._row?.Line_ID || '') : '',
                        Cost_Type_Code: e.Cost_Type_Code,
                        Value_Amount: e.amount,
                    };
                });

            const jOC = openCommitments.length ? openCommitments : [];
            const jCH = costHistory.length ? costHistory : [];
            const dzPhases = Object.values(ledger.costCodeSeeds);
            const d = jOC.length && jCH.length ? [...jOC, ...jCH] : jOC.length ? jOC : jCH;
            const d2 = dzPhases.length && d.length ? [...d, ...dzPhases] : d.length ? d : dzPhases;
            const threeDataSources = d2.length && userRows.length ? [...d2, ...userRows] : d2.length ? d2 : userRows;

            const anyCtActive = Object.values(activeCostTypes).some(Boolean);
            const filtered = (anyCtActive
                ? threeDataSources.filter(r => !r.Cost_Type_Code || (pageDescriptions[r.Cost_Type_Code] && activeCostTypes[r.Cost_Type_Code]))
                : threeDataSources
            ).filter(r => !(hideFieldBonus && r.Cost_Type_Code === '51800'))
             .filter(r => !invIndex || rowHasVendorInvoice(r, invIndex));

            // Pass 1: build desc→code reverse map from rows that have both
            const resDesc = {};
            const descToCode = {};
            filtered.forEach(row => {
                const code = row.Purchasing_Reporting_Code_4 || row.Purchasing_Reporting_Code_1 || '';
                const desc = row.Purchasing_Reporting_Code_4_Description || row.Purchasing_Reporting_Code_1_Description || '';
                if (code && desc) { resDesc[code] = desc; descToCode[desc] = code; }
                // Equipment tier (rentals roll up into All Third Party): code → description
                const eqCode = row.Rental_Equipment_Classification_Tier_Name || row.Equipment_Classification_Tier_Name || '';
                const eqDesc = row.Equipment_Classification_Tier_Name_Description || '';
                if (eqCode && eqDesc && eqDesc !== eqCode) { resDesc[eqCode] = eqDesc; descToCode[eqDesc] = eqCode; }
            });
            // Build Vendor+PO → Resource_Code map from cost history / OC so legacy
            // user ETC entries saved without Resource_Code can inherit one.
            const vendorPoToResource = {};
            [...jOC, ...jCH].forEach(r => {
                const vendor = r.Supplier_Name || r.Supplier_AB_Name || '';
                const po = r.PO_Number || '';
                const code = r.Purchasing_Reporting_Code_4 || r.Purchasing_Reporting_Code_1 || r.Rental_Equipment_Classification_Tier_Name || r.Equipment_Classification_Tier_Name || '';
                if (vendor && po && code) {
                    const key = vendor + '^' + po;
                    if (!vendorPoToResource[key]) vendorPoToResource[key] = code;
                }
            });
            // Pass 2: assign Resource_Code, resolving desc-only rows via reverse map
            filtered.forEach(row => {
                if (!row.Discipline) row.Discipline = disciplineMap[row.Phase_Code] || '';
                const code = row.Purchasing_Reporting_Code_4 || row.Purchasing_Reporting_Code_1 || '';
                const desc = row.Purchasing_Reporting_Code_4_Description || row.Purchasing_Reporting_Code_1_Description || '';
                // Preserve Resource_Code if already set (e.g. user ETC rows carry it from the ledger)
                if (!row.Resource_Code) {
                    row.Resource_Code = code || descToCode[desc] || desc || row.Rental_Equipment_Classification_Tier_Name || row.Equipment_Classification_Tier_Name || '';
                }
                // Legacy fallback: infer from cost history via Vendor+PO
                if (!row.Resource_Code && row.Supplier_Name && row.PO_Number) {
                    row.Resource_Code = vendorPoToResource[row.Supplier_Name + '^' + row.PO_Number] || '';
                }
                if (code && desc) resDesc[code] = desc;
            });
            // Merge DB-uploaded descriptions (admin Resource Code table) over project-derived
            // descriptions so the pivot shows "CODE - Description" from the canonical source.
            const dbDesc = {};
            Object.entries(dbResourceCodes.byCode || {}).forEach(([c, info]) => {
                if (info?.description) dbDesc[c] = info.description;
            });
            setResourceDescriptions(prev => {
                const merged = { ...prev, ...resDesc, ...dbDesc };
                const changed = Object.keys(merged).some(k => merged[k] !== prev[k])
                    || Object.keys(merged).length !== Object.keys(prev).length;
                return changed ? merged : prev;
            });

            const drillPath = path.filter(p => p !== 'PO_Detail');
            // Re-key userEtcMap so tree's fresh breadcrumb (with enriched Resource_Code) matches
            const rekeyedEtcMap = rekeyEtcMapByColumns(filtered, ledger.userEtcMap, drillPath);
            let tree = buildColumnTree(filtered, drillPath, rekeyedEtcMap, expandedBreadcrumbs, ledger.dayZeroBudget);
            if (drillPath[0] === 'Resource_Code' && tree['']) {
                const n = tree[''];
                n.data.key = 'Unassigned';
                tree['Unassigned'] = n;
                delete tree[''];
            }

            enrichTreeWithEntries(tree, ledger.entriesByBreadcrumb);

            const budgetResolver = createBudgetResolver({
                dayZeroBudget: ledger.dayZeroBudget,
                costTypeBudget: ledger.costTypeBudget,
                budgetByPhaseCt: ledger.budgetByPhaseCt,
                budgetByCtPhase: ledger.budgetByCtPhase,
                budgetByResource: ledger.budgetByResource,
                budgetByResourceCt: ledger.budgetByResourceCt,
                drillColumns,
                pageCostTypes: PAGE_COST_TYPES,
                reverseDisciplineMap,
            });

            tree = calculateAllTotals(tree, pageBudget, budgetResolver);

            // Walk tree → flat budgetMap (single source of truth for ThirdPartyRow)
            const BUDGET_PATHS = new Set(['Phase_Code', 'Cost_Type_Code', 'Discipline', 'Resource_Code']);
            const bMap = {};
            const walkBudget = (nodes) => {
                Object.values(nodes).forEach(node => {
                    if (node.data?.breadcrumb && node.data.resolvedBudget != null && node.data.resolvedBudget !== '' && BUDGET_PATHS.has(node.data.path)) {
                        bMap[node.data.breadcrumb] = node.data.resolvedBudget;
                    }
                    if (node.rows) walkBudget(node.rows);
                });
            };
            walkBudget(tree);
            setBudgetMap(bMap);
            setObjectMap(tree);

            const sorted = [...userData]
                .filter(row => ['Materials','Rentals','Subcontractors','Other','All Third Party'].includes(row.Forecast_Tab) && row.Value_Amount)
                .sort((a, b) => (a.Forecast_User_Data_GUID + '').localeCompare(b.Forecast_User_Data_GUID + ''));
            setSortedUserData(sorted);
            setRebuilding(false);

        }, 100);
    }, [userData, openCommitments, costHistory, drillColumns, activeCostTypes, phaseDescriptions, disciplineMap, dbResourceCodes, hideFieldBonus, pageDescriptions, invIndex]);

    const enrichTreeWithEntries = (tree, entriesByBC) => {
        const walk = (node) => {
            if (!node || !node.data) return;
            node.data.indent = (node.data.indent || 0) * 2;
            const bc = node.data.breadcrumb;
            if (bc && entriesByBC[bc]) {
                node.data.etcEntries = entriesByBC[bc];
                const entries = entriesByBC[bc];
                if (entries.length > 0) {
                    const best = entries.find(e => e.description) || entries.find(e => e.notes) || entries[0];
                    if (best.description) node.data.description = best.description;
                    if (best.notes) node.data.notes = best.notes;
                    if (best.title) node.data.title = best.title;
                    if (node.data.detail) {
                        node.data.detail = {
                            ...node.data.detail,
                            Description: best.description || node.data.detail.Description || '',
                            Notes: best.notes || node.data.detail.Notes || '',
                            Row_Label: best.title || node.data.detail.Row_Label || '',
                        };
                        if (best.guid && !node.data.detail.Forecast_User_Data_GUID) {
                            node.data.detail = {
                                ...node.data.detail,
                                Forecast_User_Data_GUID: best.guid,
                                Forecast_Row_ID: best._row?.Forecast_Row_ID || node.data.detail.Forecast_Row_ID,
                            };
                        }
                    }
                }
            }
            if (node.rows && node.rows !== false) {
                Object.values(node.rows).forEach(walk);
            }
        };
        Object.values(tree).forEach(walk);
    };

    const calculateAllTotals = (tree, freshBudget = null, budgetResolver = null) => {
        let actualTT = 0, openTT = 0, etcTT = 0, totalTT = 0;
        const resolver = budgetResolver || createBudgetResolver({
            dayZeroBudget: phaseDayZero || {},
            costTypeBudget: {},
            budgetByPhaseCt: {},
            budgetByCtPhase: {},
            drillColumns,
            pageCostTypes: PAGE_COST_TYPES,
            reverseDisciplineMap,
        });
        Object.values(tree).forEach(costType => {
            calculateColumnTotals(costType, resolver);
            actualTT += pNum(costType.data?.actualTotal);
            openTT += pNum(costType.data?.openTotal);
            etcTT += pNum(costType.data?.etcTotal);
            totalTT += pNum(costType.data?.totalTotal);
        });
        setActualTotalTotal(fNum(actualTT));
        setOpenTotalTotal(fNum(openTT));
        setEtcTotalTotal(fNum(etcTT));
        setTotalTotalTotal(fNum(totalTT));
        const budget = freshBudget != null ? freshBudget : pNum(totalCurrentBudget);
        setVarianceBudgetTotal(fNum(computeGrandVariance(budget, totalTT)));
        return tree;
    };

    useEffect(() => {
        if (selectedJob && selectedVersion) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then(data => {
                setUserData(data?.data);
            });
        }
    }, [selectedJob, selectedVersion, phased]);

    // ── Load admin-managed Resource Code tables (version-scoped, BU_ID='all') ──
    useEffect(() => {
        if (!selectedVersion) return;
        loadResourceCodeTables({ forecastGuid: selectedVersion }).then(setDbResourceCodes);
    }, [selectedVersion]);

    const getWeekLabel = (guid) => {
        if (selectedWIP && guid === selectedWIP.Version_GUID) return 'WIP ' + moment(selectedWIP.Version_Start_DateTime).format('MMM D');
        if (versions?.length) {
            const v = versions.find(v => v.Version_GUID === guid);
            if (v) return moment(v.Version_Start_DateTime).format('MMM D');
        }
        return '';
    };

    useEffect(() => {
        if (!varianceWeeks.length || !selectedJob) return;
        const otherWeeks = varianceWeeks.filter(g => g !== selectedWIP?.Version_GUID);

        const loadWeekVersion = async (guid) => {
            setWeekLoading(prev => ({ ...prev, [guid]: true }));
            const [udRes, ocRes, chRes] = await Promise.all([
                loadUserData({ id: selectedJob, forecastGuid: guid }),
                loadJobOpenCommitmentsCombined({ id: selectedJob, versionGuid: guid }),
                loadCostHistoryCombined({ id: selectedJob, versionGuid: guid }),
            ]);
            const weekUD = udRes?.data || [];
            const weekOC = (ocRes?.data || []).map(e => {
                const d = { ...e };
                if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
                if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
                d.OC = true; d.Supplier_Name = d.Supplier_AB_Name; d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number; d.Line_ID = d.PO_Line_Number || '';
                return d;
            });
            const weekCH = (chRes?.data || []).map(d => { const c = { ...d }; c.CH = true; c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number; return c; });

            const weekLedger = buildEtcLedger(weekUD, 'All Third Party', drillColumns, disciplineMap);
            const weekUserRows = weekLedger.allEntries.filter(e => e.amount !== 0).map(e => ({ ...e._row, Phase_Code: e.Phase_Code, Supplier_Name: e.Supplier_Name, PO_Number: e.PO_Number, Cost_Type_Code: e.Cost_Type_Code }));
            const combined = [...weekOC, ...weekCH, ...weekUserRows];
            const weekDescToCode = {};
            Object.entries(resourceDescriptions).forEach(([c, d]) => { weekDescToCode[d] = c; });
            // Filter to page cost types (match current tree filtering); Field Bonus
            // drops out for comparison weeks past its own version's cutoff.
            const weekHideFB = fieldBonusRemoved(versionStartFor(versions, guid, selectedWIP));
            const filtered = combined.filter(r => !r.Cost_Type_Code || (tDescriptions[r.Cost_Type_Code] && !(weekHideFB && r.Cost_Type_Code === '51800')))
                .filter(r => !invIndex || rowHasVendorInvoice(r, invIndex));
            filtered.forEach(row => {
                if (!row.Discipline) row.Discipline = disciplineMap[row.Phase_Code] || '';
                if (!row.Resource_Code) {
                    const code = row.Purchasing_Reporting_Code_4 || row.Purchasing_Reporting_Code_1 || '';
                    const desc = row.Purchasing_Reporting_Code_4_Description || row.Purchasing_Reporting_Code_1_Description || '';
                    row.Resource_Code = code || weekDescToCode[desc] || desc || row.Rental_Equipment_Classification_Tier_Name || '';
                }
            });
            const drillPath = path.filter(p => p !== 'PO_Detail');
            const weekTree = buildColumnTree(filtered, drillPath, weekLedger.userEtcMap, {}, weekLedger.dayZeroBudget);

            let totalTT = 0;
            const etcMap = {};
            Object.values(weekTree).forEach(node => {
                calculateColumnTotals(node, weekLedger.dayZeroBudget);
                totalTT += pNum(node.data?.totalTotal);
            });
            const walkMap = (obj) => {
                Object.values(obj).forEach(row => {
                    etcMap[row.data?.breadcrumb] = isNaN(row.data?.totalTotal) ? 0 : pNum(row.data?.totalTotal);
                    if (row.rows && row.rows !== false) walkMap(row.rows);
                });
            };
            walkMap(weekTree);
            return { map: etcMap, grandTotal: totalTT };
        };

        if (selectedWIP && varianceWeeks.includes(selectedWIP.Version_GUID)) {
            const prevOC = openCommitmentsPrev.length ? openCommitmentsPrev : [];
            const prevCH = costHistoryPrev.length ? costHistoryPrev : [];
            if (userDataPrev) {
                const prevLedger = buildEtcLedger(userDataPrev, 'All Third Party', drillColumns, disciplineMap);
                const prevUserRows = prevLedger.allEntries.filter(e => e.amount !== 0).map(e => ({ ...e._row, Phase_Code: e.Phase_Code, Supplier_Name: e.Supplier_Name, PO_Number: e.PO_Number, Cost_Type_Code: e.Cost_Type_Code }));
                const combined = [...prevOC, ...prevCH, ...prevUserRows];
                const prevDescToCode = {};
                Object.entries(resourceDescriptions).forEach(([c, d]) => { prevDescToCode[d] = c; });
                // Filter to page cost types (match current tree filtering); WIP is
                // always current, so Field Bonus is past its cutoff here.
                const wipHideFB = fieldBonusRemoved(selectedWIP?.Version_Start_DateTime);
                const filtered = combined.filter(r => !r.Cost_Type_Code || (tDescriptions[r.Cost_Type_Code] && !(wipHideFB && r.Cost_Type_Code === '51800')))
                    .filter(r => !invIndex || rowHasVendorInvoice(r, invIndex));
                filtered.forEach(row => {
                    if (!row.Discipline) row.Discipline = disciplineMap[row.Phase_Code] || '';
                    if (!row.Resource_Code) {
                        const code = row.Purchasing_Reporting_Code_4 || row.Purchasing_Reporting_Code_1 || '';
                        const desc = row.Purchasing_Reporting_Code_4_Description || row.Purchasing_Reporting_Code_1_Description || '';
                        row.Resource_Code = code || prevDescToCode[desc] || desc || row.Rental_Equipment_Classification_Tier_Name || '';
                    }
                });
                const drillPath = path.filter(p => p !== 'PO_Detail');
                const prevTree = buildColumnTree(filtered, drillPath, prevLedger.userEtcMap, {}, prevLedger.dayZeroBudget);
                let totalTT = 0;
                const prevMap = {};
                Object.values(prevTree).forEach(node => {
                    calculateColumnTotals(node, prevLedger.dayZeroBudget);
                    totalTT += pNum(node.data?.totalTotal);
                });
                const walkMap = (obj) => {
                    Object.values(obj).forEach(row => {
                        prevMap[row.data?.breadcrumb] = isNaN(row.data?.totalTotal) ? 0 : pNum(row.data?.totalTotal);
                        if (row.rows && row.rows !== false) walkMap(row.rows);
                    });
                };
                walkMap(prevTree);
                setObjectMapPrev(prevMap);
                setPrevGrandTotal(totalTT);
            }
        }

        Promise.all(otherWeeks.map(async guid => {
            const result = await loadWeekVersion(guid);
            return { guid, ...result };
        })).then(results => {
            const maps = {};
            results.forEach(({ guid, map, grandTotal }) => { maps[guid] = { map, grandTotal }; });
            setWeekTotalMaps(maps);
            setWeekLoading({});
        });
    }, [varianceWeeks, selectedJob, drillColumns, userDataPrev, openCommitmentsPrev, costHistoryPrev, versions, invIndex]);

    const collapse = (index) => { setCollapseNumber(index); setCollapseAll(Math.random().toString(16).substring(1)); };

    const deleteRow = (r) => {
        setDeletingRows(prev => new Set(prev).add(r.Forecast_Row_ID));
        setTimeout(() => {
            deleteUserData({ id: selectedJob, forecastGuid: selectedVersion, lockedVersion, rowId: r.Forecast_User_Data_GUID + '', tab: r.Forecast_Tab || 'All Third Party' });
            setUserData(prev => prev.filter(item => !(item.Forecast_Row_ID === r.Forecast_Row_ID && item.Forecast_Tab === r.Forecast_Tab)));
            setDeletingRows(prev => { const s = new Set(prev); s.delete(r.Forecast_Row_ID); return s; });
        }, 400);
    };

    const exportToExcel = async () => {
        try {
            setIsExporting(true);
            // Let React paint the "EXPORTING..." state before the (synchronous, possibly
            // heavy) workbook build blocks the main thread.
            await new Promise(r => requestAnimationFrame(() => r()));

            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Third Party');

            // Summary table — one row per VENDOR, rolled up (all of a vendor's POs summed
            // into a single line), grouped under its cost code (phase) with a collapsible
            // phase subtotal and a grand-total row on top. Subtotal/total rows use
            // SUBTOTAL(9,…) so Excel totals correctly (and respects filtering) without
            // double-counting. Where a phase has no vendor breakdown, it rolls up to a
            // single cost-code line.
            worksheet.properties.outlineProperties = { summaryBelow: false, summaryRight: false };

            // A Phase | B Phase Desc | C Vendor | D Budget | E Actual | F Open | G ETC |
            // H Forecast | I Variance | J Notes
            const AMT = ['E', 'F', 'G', 'H'];
            const nz = (v) => { const n = pNum(v); return n === 0 ? null : n; };

            const headers = ['Phase Code', 'Phase Description', 'Vendor', 'Budget', 'Actual Costs', 'Open Commitments', 'ETC', 'Forecast Total', 'Variance to Budget', 'Notes'];
            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };

            // Grand-total row (row 2); formulas filled once the data range is known.
            const grandRow = worksheet.addRow(['TOTAL']);
            grandRow.font = { bold: true };

            const groupByPhase = objectMap[Object.keys(objectMap)[0] || '']?.data?.path === 'Phase_Code';
            const firstDataRow = grandRow.number + 1;

            // Emit one summary line from a node's rolled-up totals.
            const addSummaryRow = (phase, phaseDesc, node, level, vendorLabel) => {
                const d = node?.data;
                if (!d) return;
                const row = worksheet.addRow([
                    phase, phaseDesc,
                    vendorLabel != null ? vendorLabel : (d.key || d.title || ''),
                    null, nz(d.actualTotal), nz(d.openTotal), nz(d.etcTotal), nz(d.totalTotal), null, d.notes || '',
                ]);
                row.outlineLevel = level;
            };

            if (groupByPhase) {
                Object.keys(objectMap)
                    .sort((a, b) => (a || '').localeCompare(b || ''))
                    .forEach(key => {
                        const phaseNode = objectMap[key];
                        const pd = phaseNode?.data;
                        if (!pd) return;
                        const phase = pd.key || '';
                        const phaseDesc = phaseDescriptions?.[phase] || '';
                        const vendors = (phaseNode.rows && phaseNode.rows !== false)
                            ? Object.values(phaseNode.rows).sort((a, b) => ('' + a?.data?.key).localeCompare('' + b?.data?.key))
                            : [];

                        // Cost-code (phase) subtotal row on top, then one row per vendor.
                        const subRow = worksheet.addRow([]);
                        subRow.outlineLevel = 0;
                        subRow.font = { bold: true };
                        subRow.getCell(1).value = phase;
                        subRow.getCell(2).value = phaseDesc;
                        subRow.getCell(3).value = 'Subtotal';
                        const b = nz(pd.resolvedBudget); if (b != null) subRow.getCell(4).value = b;
                        const v = nz(pd.varianceBudget); if (v != null) subRow.getCell(9).value = v;

                        const start = subRow.number + 1;
                        if (vendors.length) {
                            vendors.forEach(vn => addSummaryRow(phase, phaseDesc, vn, 1));
                        } else {
                            addSummaryRow(phase, phaseDesc, phaseNode, 1, '');
                        }
                        const end = worksheet.rowCount;
                        if (end >= start) AMT.forEach(col => { subRow.getCell(col).value = { formula: `SUBTOTAL(9,${col}${start}:${col}${end})` }; });
                    });
            } else {
                // No phase level in the pivot → one row per top-level (vendor) node.
                Object.keys(objectMap)
                    .sort((a, b) => (a || '').localeCompare(b || ''))
                    .forEach(key => addSummaryRow('', '', objectMap[key], 0));
            }

            const lastDataRow = worksheet.rowCount;
            if (lastDataRow >= firstDataRow) {
                // SUBTOTAL(9,…) ignores the nested phase-subtotal cells, so amounts sum only
                // the vendor rows and budget/variance sum only the phase rows.
                ['D', ...AMT, 'I'].forEach(col => {
                    grandRow.getCell(col).value = { formula: `SUBTOTAL(9,${col}${firstDataRow}:${col}${lastDataRow})` };
                });
            }
            grandRow.eachCell(cell => { cell.border = { bottom: { style: 'thin' }, top: { style: 'thin' } }; });

            [4, 5, 6, 7, 8, 9].forEach(c => { worksheet.getColumn(c).numFmt = '#,##0'; });
            const widths = [14, 32, 30, 14, 14, 16, 12, 14, 16, 44];
            worksheet.columns.forEach((col, i) => { col.width = widths[i] || 14; });
            worksheet.views = [{ state: 'frozen', ySplit: 2 }];
            worksheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: headers.length } };

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ThirdParty_${selectedJob}_${moment().format('YYYYMMDD')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
            try { window.alert('Export failed: ' + (err?.message || err)); } catch (e) {}
        } finally {
            setIsExporting(false);
        }
    };

    // ── tabConfig for ThirdPartyRow ──
    const allThirdPartyTabConfig = useMemo(() => ({
        tab: 'All Third Party',
        defaultCostType: '53100',
        showCostTypeDropdown: true,
        costTypeDescriptions: pageCostTypeDescriptions,
        stickyTitle: true,
        editableTitle: false,
        hasTCaretIndent: true,
        deferredSave: false,
        poDetailTitlePriority: 'poLine',
        lineIdFallback: 'standard',
        costCodeFallback: false,
        costTypeFromData: false,
        fixedCostType: null,
        marginLeftOffset: 0,
        collapsableChildPadding: 22,
        newPExtraFields: null,
        budgetInit: (r) => {
            const val = budgetMap[r?.breadcrumb];
            if (val != null && val !== '' && val !== 0) return fNum(val);
            return '';
        },
        budgetDisplay: (props, r, currentBudget, userSaved) => {
            if (props.newRow) return '';
            return currentBudget;
        },
        renderDetailColumns: (r, rowData, s, fNum, moment, percentCleaned) => (
            <View style={{ flexDirection: 'row' }}>
                <Text style={s().notEdit}>{(r?.path === 'PO_Detail' || r?.path === 'Line_ID') && rowData?.OC ? moment(rowData?.Order_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{(r?.path === 'PO_Detail' || r?.path === 'Line_ID') && rowData?.OC ? moment(rowData?.Rental_Start_Date).diff(rowData?.Order_Date, 'days') : ''}</Text>
                <Text style={s().notEdit}>{(r?.path === 'PO_Detail' || r?.path === 'Line_ID') && rowData?.OC ? moment(rowData?.Rental_Start_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{(r?.path === 'PO_Detail' || r?.path === 'Line_ID') && rowData?.OC ? moment(rowData?.Rental_End_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? rowData?.Unit_Of_Measure_Description : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity_Received) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity_Open) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? percentCleaned : ''}</Text>
            </View>
        ),
    }), [budgetMap, drillColumns, pageCostTypeDescriptions]);

    const phaseCCDayZero = {};

    const lookupOptions = useMemo(() => {
        const vendorSet = new Set();
        const poSet = new Set();
        [...openCommitments, ...costHistory].forEach(r => {
            if (r.Supplier_Name && r.Supplier_Name !== 'Unknown Vendor') vendorSet.add(r.Supplier_Name);
            if (r.PO_Number && r.PO_Number !== 'Unknown PO') poSet.add('' + r.PO_Number);
        });
        (userData || []).forEach(r => {
            if (r.Forecast_Tab === 'All Third Party') {
                if (r.Supplier_Name) vendorSet.add(r.Supplier_Name);
                if (r.PO_Number) poSet.add('' + r.PO_Number);
            }
        });
        const phaseResCount = {};
        [...openCommitments, ...costHistory].forEach(r => {
            const phase = r.Phase_Code || r.Cost_Code || '';
            const res = r.Purchasing_Reporting_Code_4 || r.Purchasing_Reporting_Code_1 || '';
            if (phase && res) {
                if (!phaseResCount[phase]) phaseResCount[phase] = {};
                phaseResCount[phase][res] = (phaseResCount[phase][res] || 0) + 1;
            }
        });
        const phaseResourceMap = {};
        Object.keys(phaseResCount).forEach(p => {
            const best = Object.entries(phaseResCount[p]).sort((a, b) => b[1] - a[1])[0];
            if (best) phaseResourceMap[p] = best[0];
        });
        // Prefer admin-uploaded Resource Code table filtered to this page's cost types.
        // This page spans Materials (53xxx), Subcontractors (54xxx), Rentals (52xxx), Other, etc.
        const resourceDropdown = buildResourceDropdownFromDb({
            byCode: dbResourceCodes.byCode || {},
            codesByCostType: dbResourceCodes.codesByCostType || {},
            allowedCostTypes: Object.keys(pageDescriptions),
            projectResources: resourceDescriptions || {},
            budgetByResource: {},
            fallback: resourceDescriptions || {},
        });
        return {
            phases: phaseDescriptions || {},
            costTypes: pageDescriptions,
            resources: resourceDropdown,
            vendors: [...vendorSet].sort(),
            pos: [...poSet].sort(),
            reverseDisciplineMap,
            disciplineMap,
            phaseResourceMap,
            budgetPhases: Object.keys(phaseDayZero || {}),
        };
    }, [phaseDescriptions, resourceDescriptions, openCommitments, costHistory, userData, reverseDisciplineMap, disciplineMap, phaseDayZero, dbResourceCodes, pageDescriptions]);

    const pivotPopoverProps = useMemo(() => ({
        drillColumns, tabConfig: allThirdPartyTabConfig,
        selectedJob, selectedVersion, lockedVersion,
        userData, setUserData, lookupOptions,
    }), [drillColumns, allThirdPartyTabConfig, selectedJob, selectedVersion, lockedVersion, userData, setUserData, lookupOptions]);

    const rowContextValue = useMemo(() => ({
        tabConfig: allThirdPartyTabConfig,
        s, path, drillColumns,
        expandedBreadcrumbs, setExpandedBreadcrumbs,
        objectMapPrev, weekTotalMaps, varianceWeeks, weekDisplayMode,
        selectedWIP, descriptionVisible,
        phaseDayZero, phaseCCDayZero, phaseDescriptions, titleDescriptions, resourceDescriptions,
        selectedJob, selectedVersion, lockedVersion,
        userData, setUserData, focusedId, indentColors,
        collapseAll, collapseNumber, setCollapseNumber,
        entriesByBreadcrumb, setPopoverBreadcrumb, pivotPopoverProps,
        poStatus, openTapDoc,
    }), [allThirdPartyTabConfig, s, path, drillColumns, expandedBreadcrumbs, objectMapPrev, weekTotalMaps, varianceWeeks, weekDisplayMode, selectedWIP, descriptionVisible, phaseDayZero, phaseDescriptions, titleDescriptions, resourceDescriptions, selectedJob, selectedVersion, lockedVersion, userData, focusedId, collapseAll, collapseNumber, entriesByBreadcrumb, pivotPopoverProps, poStatus, openTapDoc]);

    // ── Category grouping ──
    const getNodeCategory = (node) => {
        const ct = node?.data?.detail?.Cost_Type_Code || node?.data?.costType || '';
        if (ct && costTypeCategory[ct]) return costTypeCategory[ct];
        if (node?.rows && typeof node.rows === 'object') {
            for (const child of Object.values(node.rows)) {
                const childCat = getNodeCategory(child);
                if (childCat) return childCat;
            }
        }
        return '';
    };

    const groupObjectMapByCategory = useCallback((objMap) => {
        const categoryOrder = ['Labor', 'Equipment', 'Materials', 'Subcontractors', 'Other Expenses'];
        const groups = {};
        categoryOrder.forEach(c => { groups[c] = []; });
        const uncategorized = [];
        Object.keys(objMap).sort((a, b) => a ? (b ? a.localeCompare(b) : -1) : 1).forEach((key) => {
            const cat = getNodeCategory(objMap[key]);
            if (cat && groups[cat]) {
                groups[cat].push(key);
            } else {
                uncategorized.push(key);
            }
        });
        return { groups, uncategorized, categoryOrder };
    }, []);

    const getCategorySectionTotals = (keys) => {
        let actual = 0, open = 0, etc = 0, total = 0;
        keys.forEach(key => {
            const node = objectMap[key];
            actual += pNum(node?.data?.actualTotal);
            open += pNum(node?.data?.openTotal);
            etc += pNum(node?.data?.etcTotal);
            total += pNum(node?.data?.totalTotal);
        });
        return { actual, open, etc, total };
    };

    const topLevelNewRow = {
        rows: false,
        data: { path: path[0], detail: {}, indent: 0, breadcrumb: 't' }
    };

    // ── ValidationArea ──
    const levelColor = (lvl) => {
        const colors = { 'Phase': '#3b82f6', 'Vendor': '#8b5cf6', 'PO': '#f59e0b', 'Line': '#10b981', 'Cost Type': '#ef4444', 'User Row': '#6366f1', 'Top Level': '#64748b' };
        return colors[lvl] || '#6b7280';
    };

    const descriptionText = (row) => [
        row.Cost_Code && row.Cost_Code !== 'undefined' && !row.Cost_Code.includes('^') ? (row.Cost_Code + (phaseDescriptions?.[row.Cost_Code] ? ' - ' + phaseDescriptions[row.Cost_Code] : '')) : null,
        row.Supplier_Name && row.Supplier_Name !== 'undefined' && !row.Supplier_Name.includes('^') ? row.Supplier_Name : null,
        row.PO_Number && row.PO_Number !== 'undefined' && !(row.PO_Number + '').includes('^') ? row.PO_Number : null,
    ].filter(Boolean).join(' \u203A ');

    const getRowLevel = (row) => {
        if (row.Equipment_Tier && row.Equipment_Tier !== '' && row.Equipment_Tier !== 'undefined') return row.Equipment_Tier;
        if (row.Forecast_Row_ID?.includes('^~u')) return 'User Row';
        if (row.Forecast_Row_ID?.includes('t^')) return 'Top Level';
        const colMap = { 'Phase_Code': row.Cost_Code, 'Supplier_Name': row.Supplier_Name, 'PO_Number': row.PO_Number, 'Cost_Type_Code': row.Cost_Type, 'Line_ID': '' };
        const labelMap = { 'Phase_Code': 'Phase', 'Supplier_Name': 'Vendor', 'PO_Number': 'PO', 'Line_ID': 'Line', 'Cost_Type_Code': 'Cost Type' };
        for (let i = drillColumns.length - 1; i >= 0; i--) {
            const val = colMap[drillColumns[i]];
            if (val && val !== 'undefined' && !val.includes('^')) return labelMap[drillColumns[i]] || drillColumns[i];
        }
        return 'Phase';
    };

    const groupByEACCategory = (data) => {
        const catOrder = ['Labor', 'Equipment', 'Materials', 'Subcontractors', 'Other Expenses'];
        const groups = {};
        catOrder.forEach(c => { groups[c] = []; });
        (data || []).forEach(row => {
            if (['Materials','Rentals','Subcontractors','Other','All Third Party'].includes(row.Forecast_Tab) && row.Value_Amount) {
                const cat = getCostTypeCategory(row.Cost_Type);
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(row);
            }
        });
        return groups;
    };

    const ValidationArea = () => {
        const totalAllRows = (sortedUserData || [])
            .filter(row => ['Materials','Rentals','Subcontractors','Other','All Third Party'].includes(row.Forecast_Tab) && row.Value_Amount)
            .reduce((sum, row) => sum + pNum(row.Value_Amount), 0);

        const cols = [
            { label: 'Code', w: 70 },
            { label: 'Title', w: 110 },
            { label: 'Path', w: 180 },
            { label: 'Description', w: 140 },
            { label: 'Notes', w: 130 },
            { label: 'ETC', w: 110, align: 'right' },
            { label: '', w: 40 },
        ];

        const headerRowEl = (soft) => (
            <View style={{ flexDirection: 'row', backgroundColor: soft ? '#edf0f4' : '#1e293b', paddingHorizontal: 4, paddingLeft: 24, height: 18, alignItems: 'center' }}>
                {cols.map((col, i) => (
                    <Text key={i} style={{ width: col.w, fontSize: soft ? 10 : 11, fontWeight: '600', color: soft ? '#64748b' : '#f1f5f9', textAlign: col.align || 'left', paddingHorizontal: 8 }}>{col.label}</Text>
                ))}
            </View>
        );

        const rowBreadcrumb = (row) => {
            const colMap = { Phase_Code: row.Cost_Code || '', Supplier_Name: row.Supplier_Name || '', PO_Number: row.PO_Number || '', Cost_Type_Code: row.Cost_Type || '', Discipline: '', Resource_Code: row.Resource_Code || '', Line_ID: '' };
            let bc = '';
            for (let i = 0; i < drillColumns.length; i++) {
                const v = colMap[drillColumns[i]] || '';
                if (!v || v === 'undefined' || v.includes('^')) break;
                bc += '^' + v;
            }
            return bc;
        };

        const dataRow = (row, idx) => {
            const bc = rowBreadcrumb(row);
            return (
                <View key={row.Forecast_User_Data_GUID || row.Forecast_Row_ID} style={{
                    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4, paddingLeft: 24, height: 22,
                    backgroundColor: deletingRows.has(row.Forecast_Row_ID) ? '#fee2e2' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                    borderBottom: '1px solid #f1f5f9',
                    opacity: deletingRows.has(row.Forecast_Row_ID) ? 0.5 : 1,
                    transition: 'opacity 0.3s, background-color 0.3s',
                }}>
                    <Text style={{ width: 70, fontSize: 12, color: '#475569', paddingHorizontal: 8, fontFamily: 'monospace' }}>{row.Cost_Type}</Text>
                    <Text style={{ width: 110, fontSize: 12, color: '#1e293b', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Row_Label || ''}</Text>
                    <View style={{ width: 180, paddingHorizontal: 8, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Text style={{ fontSize: 12, color: '#1e293b', lineHeight: 18, flex: 1 }} numberOfLines={1}>{descriptionText(row)}</Text>
                    </View>
                    <Text style={{ width: 140, fontSize: 12, color: '#1e293b', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Description || ''}</Text>
                    <Text style={{ width: 130, fontSize: 12, color: '#475569', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Notes || ''}</Text>
                    <Text style={{ width: 110, fontSize: 12, fontWeight: '500', color: '#1e293b', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(row.Value_Amount)}</Text>
                    <View style={{ width: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <Pressable style={{ cursor: 'pointer' }} onPress={() => setPopoverBreadcrumb(bc)}>
                            <Icon3 name="MdEdit" color="#3b82f6" size={14} />
                        </Pressable>
                        <Pressable style={{ cursor: deletingRows.has(row.Forecast_Row_ID) ? 'default' : 'pointer' }}
                            onPress={() => !deletingRows.has(row.Forecast_Row_ID) && deleteRow(row)}>
                            {deletingRows.has(row.Forecast_Row_ID)
                                ? <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(0,0,0,0.1)', borderTop: '2px solid #dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                : <Icon3 name="MdDeleteOutline" color="#dc2626" size={14} />}
                        </Pressable>
                    </View>
                </View>
            );
        };

        const groupedByCategory = groupByEACCategory(sortedUserData);

        return (
            <View style={{ marginTop: 16 }}>
                <View style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 820 }}>
                    <View style={{ padding: 10, borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>ETC Values Summary</Text>
                    </View>
                    {headerRowEl()}
                    {Object.keys(groupedByCategory).map(category => {
                        const rows = groupedByCategory[category];
                        if (rows.length === 0) return null;
                        const catTotal = rows.reduce((s, row) => s + pNum(row.Value_Amount), 0);
                        const colors = categoryColors[category] || categoryColors['Other Expenses'];
                        return (
                            <View key={category}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 3, paddingHorizontal: 8, backgroundColor: colors.bg, borderTop: '1px solid ' + colors.border }}>
                                    <View style={{ backgroundColor: colors.badge, borderRadius: 3, paddingHorizontal: 6, paddingVertical: 1, marginRight: 8 }}>
                                        <Text style={{ fontSize: 9, fontWeight: '700', color: 'white' }}>{category.toUpperCase()}</Text>
                                    </View>
                                    <Text style={{ fontSize: 11, fontWeight: '600', color: colors.text }}>{fNum(catTotal)}</Text>
                                </View>
                                {rows.map((row, idx) => dataRow(row, idx))}
                            </View>
                        );
                    })}
                    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4, paddingLeft: 24, backgroundColor: '#eff6ff', borderTop: '2px solid #bfdbfe', height: 18 }}>
                        <Text style={{ width: 630, fontSize: 13, fontWeight: '700', color: '#1d4ed8', paddingHorizontal: 8 }}>Grand Total</Text>
                        <Text style={{ width: 110, fontSize: 14, fontWeight: '800', color: '#1d4ed8', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(totalAllRows)}</Text>
                        <View style={{ width: 40 }} />
                    </View>
                </View>
            </View>
        );
    };

    // ── RENDER ──
    const { groups: catGroups, categoryOrder } = useMemo(() => groupObjectMapByCategory(objectMap), [objectMap, groupObjectMapByCategory]);

    const renderCategorySection = (category, keys) => {
        if (!keys || !keys.length) return null;
        const colors = categoryColors[category] || categoryColors['Other Expenses'];
        const sectionTotals = getCategorySectionTotals(keys);
        return (
            <View key={category} style={{ marginTop: 14, marginBottom: 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingLeft: 18, paddingRight: 10, borderLeft: '4px solid ' + colors.badge, backgroundColor: colors.bg, borderRadius: 3, marginBottom: 2, gap: 12 }}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: colors.text, letterSpacing: 0.3 }}>{category.toUpperCase()}</Text>
                    <Text style={{ fontSize: 11, color: colors.text, opacity: 0.7 }}>Total: {fNum(sectionTotals.total)}</Text>
                </View>
                {keys.map((phase) => (
                    <View key={phase} style={{ marginTop: 10 }}>
                        {phase?.includes('t^')
                            ? <View style={{ marginLeft: 22 }}><ThirdPartyRow data={objectMap[phase]} /></View>
                            : <PivotRowCollapsable data={objectMap[phase]} />
                        }
                    </View>
                ))}
            </View>
        );
    };

    return (
        <View style={{ margin: 30, marginTop: 0, marginRight: 200, marginBottom: 200 }}>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            <View style={{ marginBottom: 8, flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: '4px 10px', backgroundColor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#475569', letterSpacing: 0.3 }}>Vendor Invoice:</Text>
                    {[
                        { on: false, label: 'All', tooltip: 'Show every line' },
                        { on: true, label: 'Received', tooltip: 'Only PO lines JDE has vouchered against a vendor invoice, plus direct (non-PO) vouchers' },
                    ].map(o => {
                        const isActive = invoicedOnly === o.on;
                        return (
                            <Pressable key={o.label} title={o.tooltip}
                                onPress={() => setInvoicedOnly(o.on)}
                                style={{ cursor: 'pointer', backgroundColor: isActive ? '#1d4ed8' : '#f1f5f9', borderRadius: 4, padding: '3px 7px', border: isActive ? '1px solid #1d4ed8' : '1px solid #cbd5e1', transition: 'all 0.15s ease' }}>
                                <Text style={{ fontSize: 11, fontWeight: '700', color: isActive ? '#fff' : '#475569' }}>{o.label}</Text>
                            </Pressable>
                        );
                    })}
                </View>
                <PivotSelector columns={drillColumns} available={availablePivotColumns} onChange={setDrillColumns} />
                <VarianceWeekSelector selectedWeeks={varianceWeeks} onChange={setVarianceWeeks} />
                <ReversedVoucherLegend tree={objectMap} />
            </View>

            <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 20, paddingTop: 0, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3, position: 'relative' }}>
                {rebuilding && (
                    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 200, alignItems: 'center', justifyContent: 'center' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'white', padding: '8px 16px', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                            <Text style={{ fontSize: 12, fontWeight: '600', color: '#6b7280' }}>Rebuilding tree…</Text>
                        </View>
                    </View>
                )}
                <View style={{ backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 100, paddingTop: 20, width: 'max-content', minWidth: '100%' }}>
                    <View style={{ flexDirection: 'row', marginLeft: 22 }}>
                        <View style={{ flexDirection: 'row', position: 'sticky', left: 22, zIndex: 12 }}>
                            <View style={[s(300).title, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
                                <Pressable
                                    style={{ flexDirection: 'row', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 3, border: '1px solid #d1d5db', cursor: 'pointer', backgroundColor: '#f9fafb' }}
                                    onPress={() => collapse(1)}>
                                    <Icon3 name="MdUnfoldLess" size={11} color="#6b7280" />
                                    <Text style={{ color: '#6b7280', fontSize: 11, fontWeight: '600' }}>Collapse</Text>
                                </Pressable>
                            </View>
                            <View style={[s(140).title, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }]}>
                                <Text style={{ fontSize: 12 }}>Description</Text>
                                <Icon2
                                    onPress={() => setDescriptionVisible(!descriptionVisible)}
                                    name={descriptionVisible ? 'chevron-down-circle-outline' : 'chevron-forward-circle-outline'}
                                    size={16}
                                />
                            </View>
                        </View>
                        {descriptionVisible ? <View style={{ flexDirection: 'row' }}>
                            <Text style={s().title}>Order Date</Text>
                            <Text style={s().title}>Days Since PO</Text>
                            <Text style={s().title}>Start Date</Text>
                            <Text style={s().title}>End Date</Text>
                            <Text style={s().title}>UoM</Text>
                            <Text style={s().title}>Ordered</Text>
                            <Text style={s().title}>Received</Text>
                            <Text style={s().title}>Open</Text>
                            <Text style={s().title}>% Cleaned</Text>
                            <Text style={s().title}>Invoice</Text>
                        </View> : null}
                        <View style={{ width: 10 }} />
                        <Text style={s().title}>Current Budget</Text>
                        <View style={{ width: 10 }} />
                        <Text style={s().title}>Most Recent Cost Date</Text>
                        <Text style={s().title}>Actual Costs</Text>
                        <Text style={s().title}>Open</Text>
                        <Text style={s(120).title}>Estimate to Complete (ETC)</Text>
                        <Text style={s().title}>Total</Text>
                        <Text style={s().title}>Variance to Budget (+)/-</Text>
                        {varianceWeeks.map(guid => (
                            <View key={guid} style={[s().title, { backgroundColor: '#eef2ff', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }]}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                    <Text style={{ color: '#4338ca', fontSize: 12, fontWeight: '700' }}>{getWeekLabel(guid)}</Text>
                                    {weekLoading[guid] ? <Text style={{ fontSize: 10, color: '#818cf8', animation: 'spin 1s linear infinite' }}>⟳</Text> : null}
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                    <Pressable onPress={() => setWeekDisplayMode(prev => ({ ...prev, [guid]: 'total' }))} style={{ cursor: 'pointer', backgroundColor: (weekDisplayMode[guid] || 'var') === 'total' ? '#4338ca' : '#e0e7ff', borderRadius: 3, padding: '1px 4px' }}>
                                        <Text style={{ fontSize: 8, fontWeight: '700', color: (weekDisplayMode[guid] || 'var') === 'total' ? 'white' : '#6366f1' }}>TOT</Text>
                                    </Pressable>
                                    <Pressable onPress={() => setWeekDisplayMode(prev => ({ ...prev, [guid]: 'var' }))} style={{ cursor: 'pointer', backgroundColor: (weekDisplayMode[guid] || 'var') === 'var' ? '#4338ca' : '#e0e7ff', borderRadius: 3, padding: '1px 4px' }}>
                                        <Text style={{ fontSize: 8, fontWeight: '700', color: (weekDisplayMode[guid] || 'var') === 'var' ? 'white' : '#6366f1' }}>VAR</Text>
                                    </Pressable>
                                </View>
                            </View>
                        ))}
                        <Text style={s(120).title}>Cost Type</Text>
                        <View style={{ flexDirection: 'row' }}>
                            <Text style={s(400).title}>Notes</Text>
                            <Pressable onPress={() => !isExporting && exportToExcel()}>
                                <Text style={{ backgroundColor: isExporting ? '#c0c0c0' : '#4CAF50', color: 'white', borderRadius: 2, textAlign: 'center', padding: 5, margin: 2, fontSize: 12 }}>{isExporting ? 'EXPORTING...' : 'EXPORT'}</Text>
                            </Pressable>
                        </View>
                    </View>

                    <View style={{ flexDirection: 'row', marginLeft: 22 }}>
                        <View style={{ position: 'sticky', left: 22, zIndex: 12, backgroundColor: 'white' }}>
                            <Text style={[s(descriptionVisible ? 1441 : 441).notEdit, { textAlign: 'left', backgroundColor: '#f0f0f0' }]}>Totals</Text>
                        </View>
                        <View style={{ width: 10, backgroundColor: 'white' }} />
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{totalCurrentBudget}</Text>
                        <View style={{ width: 110, backgroundColor: 'white' }} />
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{actualTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{openTotalTotal}</Text>
                        <Text style={[s(120).notEdit, { backgroundColor: '#f0f0f0' }]}>{etcTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{totalTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{varianceBudgetTotal}</Text>
                        {varianceWeeks.map(guid => {
                            const gt = guid === selectedWIP?.Version_GUID ? prevGrandTotal : weekTotalMaps[guid]?.grandTotal;
                            const mode = weekDisplayMode[guid] || 'var';
                            const displayGt = mode === 'var' && gt != null ? pNum(totalTotalTotal) - pNum(gt) : gt;
                            return (
                                <Text key={guid} style={[s().notEdit, { backgroundColor: mode === 'var' ? '#fef9ee' : '#eef2ff' }]}>
                                    {displayGt != null ? fNum(displayGt) : ''}
                                </Text>
                            );
                        })}
                    </View>
                </View>

                <ThirdPartyRowContext.Provider value={rowContextValue}>
                    <PopoverBreadcrumbContext.Provider value={popoverBreadcrumb}>
                        {drillColumns[0] === 'Cost_Type_Code'
                            ? categoryOrder.map(cat => renderCategorySection(cat, catGroups[cat]))
                            : Object.keys(objectMap).sort((a, b) => (a || '').localeCompare(b || '')).map(key => (
                                <View key={key} style={{ marginTop: 10 }}>
                                    {key?.includes('t^')
                                        ? <View style={{ marginLeft: 22 }}><ThirdPartyRow data={objectMap[key]} /></View>
                                        : <PivotRowCollapsable data={objectMap[key]} />
                                    }
                                </View>
                            ))
                        }
                        <View style={{ marginTop: 10 }}>
                            <View style={{ marginLeft: 22 }}><ThirdPartyRow newRow={true} data={topLevelNewRow} /></View>
                        </View>
                        <EtcPopoverHost
                            objectMap={objectMap}
                            popoverBreadcrumb={popoverBreadcrumb}
                            setPopoverBreadcrumb={setPopoverBreadcrumb}
                            entriesByBreadcrumb={entriesByBreadcrumb}
                            pivotPopoverProps={pivotPopoverProps}
                            topLevelNodeData={topLevelNewRow.data}
                        />
                    </PopoverBreadcrumbContext.Provider>
                </ThirdPartyRowContext.Provider>
            </View>

            {/* <FuelAnalysis /> */}

            <ValidationArea />
            {viewerNode}
        </View>
    );
};

export default AllThirdParty;
